<template>
  <TemperatureOutputPlotView v-if="showChart" />
  <q-toolbar>
    <q-toolbar-title class="text-primary">
      Derived from Acoustic Power and Timing input parameters
    </q-toolbar-title>
  </q-toolbar>
  <q-card-section class="row flex justify-center q-gutter-md">
    <q-input
      class="col-12"
      :dark="isDarkModeGlobal"
      outlined
      readonly
      label="TIC in the skull [mW/cm]"
      :model-value="calculatorStore.getTIC.toFixed(2)"
    >
      <template #append>
        <reference-popup-proxy
          icon="help"
          size="md"
          tooltip="Show note"
          :text="`Cranial Thermal Index (TIC): The TIC is calculated using this formula:
                          <math xmlns='http://www.w3.org/1998/Math/MathML' display='inline'>
                            <mi>TIC</mi>
                            <mo>=</mo>
                            <mfrac>
                              <mrow>
                                <msub><mi>W</mi><mn>o</mn></msub>
                                <mo>/</mo>
                                <msub><mi>D</mi><mrow><mi>e</mi><mi>q</mi></mrow></msub>
                              </mrow>
                              <msub><mi>C</mi><mrow><mi>T</mi><mi>I</mi><mi>C</mi></mrow></msub>
                            </mfrac>
                          </math>
                          , where W<sub>o</sub> is the transducer output power in mW (or acoustic power), D<sub>eq</sub> is the diameter of the active aperture of the transducer in cm (please see
                          <a href='https://doi.org/10.1016/j.brs.2025.10.007' target='_blank'>Aubry et al. 2025</a> for a figure depicting D<sub>eq</sub>) and C<sub>TIC</sub> is a constant equal to 40 mW/cm.
                          <br><br>
                          As stated in <a href='https://doi.org/10.1016/j.brs.2025.10.007' target='_blank'>Aubry et al. 2025</a>, TIC assumes the bone is very close to the front face of the diagnostic ultrasound probe.
                          It further makes the conservative assumption of complete absorption of ultrasound energy at the bone surface.
                          <br><br>
                          <a href='https://doi.org/10.1016/j.brs.2025.10.007' target='_blank'>Aubry et al. 2025</a> recommend that the window for averaging the acoustic power should not exceed 30
                          seconds, due to the characteristic diffusion time of the skull bone. Therefore, if the stimulation exceeds 30 seconds, the average acoustic power is calculated for consecutive 30s windows, and the reported TIC is calculated using the highest average acoustic power across the windows. If the stimulation lasts less than 30 seconds, the remaining time, up to 30 seconds,
                           is treated as off time for the averaging window.`"
        />
      </template>
    </q-input>
  </q-card-section>
  <q-toolbar>
    <q-toolbar-title class="text-primary">
      Derived SAR estimates [Work in Progress]
      <q-tooltip>Work in progress</q-tooltip>
    </q-toolbar-title>
  </q-toolbar>
  <q-card-section class="row flex justify-center q-gutter-md">
    <q-input
      class="col-12"
      :dark="isDarkModeGlobal"
      label="Total SAR Averaged over 10 gm or 10 cc [W/kg] (needs to be <X)"
      outlined
      type="number"
      readonly
      step="0.000001"
      :model-value="calculatorStore.totalSARAveragedOver10gmOr10ccWkg.toFixed(2)"
    >
      <template #append>
        <reference-popup-proxy
          icon="help"
          size="md"
          tooltip="Show note"
          text="Total SAR due to concurrent MR imaging and TUS, calculated by adding TUS SAR at focus averaged over 10 cc and MR SAR averaged over 10 cc."
        />
      </template>
    </q-input>

    <q-input
      class="col-12"
      :dark="isDarkModeGlobal"
      label="SAR for Temperature Calculation [W/kg]"
      outlined
      type="number"
      readonly
      step="0.000001"
      :model-value="calculatorStore.SAR_for_Temperature_Calculation.toFixed(2)"
    >
      <template #append>
        <reference-popup-proxy
          icon="help"
          size="md"
          tooltip="Show note"
          text="Total SAR due to concurrent MR imaging and TUS, calculated by adding TUS maximum SAR at focus and MR SAR averaged over 10 cc. This represents a worst-case scenario with no spatial smoothing of the TUS SAR."
        />
      </template>
    </q-input>
  </q-card-section>
</template>
<script setup lang="ts">
import { useCalculatorStore } from '../stores/calculator.store'
import ReferencePopupProxy from "./PopupProxyLiteratureReference.vue"
import {storeToRefs} from "pinia"
import TemperatureOutputPlotView from "./TemperatureOutputPlotView.vue";
import {nextTick, ref, watch} from "vue";
const calculatorStore = useCalculatorStore()
const { isDarkModeGlobal } = storeToRefs(calculatorStore)
const showChart = ref(true)
// Recreate the charts when toggling dark mode
const toggleDarkMode = () => {
  showChart.value = false; // Destroy the component
  nextTick(() => {
    showChart.value = true; // Recreate the component after it’s destroyed
  });
};

watch(isDarkModeGlobal, () => {
  console.log("toggleDarkMode");
  toggleDarkMode()
})
</script>
